import React from "react";
import { useLang } from "../LangContext";
import { motion } from "framer-motion";

const CVS_IMAGE = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/d668e85a-16ce-43ee-9dde-9e804f77eac5.png`;

export default function Legacy() {
    const { t } = useLang();
    const l = t.legacy;
    return (
        <section data-testid="legacy-section" id="inspiration" className="py-24 md:py-32 px-6 md:px-12 bg-ink">
            <div className="max-w-6xl mx-auto">
                <div className="mb-12">
                    <span className="text-xs font-mono uppercase tracking-[0.3em] text-volt">— {l.label}</span>
                    <h2 className="font-graffiti text-4xl md:text-5xl lg:text-6xl tracking-tight mt-6 text-white leading-[0.95]">
                        {l.title}
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <div className="sticky top-28">
                            <div className="relative">
                                {/* volt offset frame */}
                                <div className="absolute -top-3 -left-3 w-full h-full border-2 border-volt/60 pointer-events-none" />
                                <div className="aspect-[3/4] w-full overflow-hidden border border-white/10 relative group">
                                    <img
                                        src={CVS_IMAGE}
                                        alt={l.title}
                                        data-testid="legacy-portrait"
                                        className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-[1.02] transition-all duration-700"
                                    />
                                    {/* caption */}
                                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent px-5 pb-4 pt-16">
                                        <div className="font-graffiti text-2xl text-white leading-none">Constant Vanden Stock</div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-volt mt-1.5">1924 — 2008</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7 space-y-6 text-zinc-300 text-base md:text-lg leading-relaxed">
                        <p>{l.body1}</p>
                        <p>{l.body2}</p>
                        <p>{l.body3}</p>
                    </div>
                </div>

                {/* Cruyff spotlight — full-width statement */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="mt-20 md:mt-28 border border-volt/40 bg-gradient-to-br from-volt/[0.08] to-transparent p-8 md:p-14 relative overflow-hidden"
                    data-testid="cruyff-spotlight"
                >
                    <div className="absolute -top-10 -right-6 font-graffiti text-[180px] md:text-[280px] leading-none text-volt/10 select-none pointer-events-none">"</div>
                    <blockquote className="relative">
                        <p className="font-graffiti text-4xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight max-w-4xl">
                            {l.cruyff_quote}
                        </p>
                        <footer className="mt-8 flex items-center gap-4">
                            <div className="h-px w-16 bg-volt" />
                            <span className="font-mono text-xs uppercase tracking-[0.3em] text-volt">
                                {l.cruyff_author}
                            </span>
                        </footer>
                        <p className="mt-6 text-zinc-300 text-base md:text-lg leading-relaxed max-w-2xl">
                            {l.cruyff_context}
                        </p>
                    </blockquote>
                </motion.div>

                {/* Mission narrative continues */}
                <div className="mt-16 md:mt-20 max-w-3xl space-y-6 text-zinc-300 text-base md:text-lg leading-relaxed">
                    <p>{l.body4}</p>
                    <p>{l.body5}</p>
                    <p>{l.body6}</p>
                    <p>{l.body7}</p>
                </div>

                {/* Steegen personal testimonial */}
                <div
                    data-testid="steegen-testimonial"
                    className="mt-16 border-l-2 border-volt pl-6 md:pl-10 py-2 max-w-3xl"
                >
                    <p className="text-white text-lg md:text-xl leading-relaxed italic">
                        "{l.steegen_quote}"
                    </p>
                    <footer className="mt-4 font-mono text-xs uppercase tracking-[0.3em] text-volt">
                        — {l.steegen_author}
                    </footer>
                </div>

                <div className="mt-12">
                    <a href="#donate" data-testid="legacy-cta" className="btn-volt inline-flex items-center gap-2 px-6 py-3 text-xs w-fit">
                        {t.nav.help} →
                    </a>
                </div>
            </div>
        </section>
    );
}
